import { api } from '@/utils/api';
import { uploadToR2 } from '@/utils/uploadUtils';
import { useEffect, useRef } from 'react';
import { MediaItem } from './usePostForm';

type SharedMediaItem = {
  type: string;
  path: string;
  value?: string;
  mimeType?: string;
  fileContent?: string;
};

type UseSharedMediaProps = {
  isOpen: boolean;
  sharedMedia?: SharedMediaItem[];
  uppy: any; // Using any to avoid complex Uppy type issues
  userProfileId?: number;
  setMedia: React.Dispatch<React.SetStateAction<MediaItem[]>>;
};

export function useSharedMedia({ isOpen, sharedMedia, uppy, userProfileId, setMedia }: UseSharedMediaProps) {
  // Use a ref to track if we've already processed this batch of shared media
  const processedBatchRef = useRef<string>("");
  
  useEffect(() => {
    if (isOpen && sharedMedia && sharedMedia.length > 0) {
      // Create a unique identifier for this batch of shared media
      const batchId = sharedMedia.map(item => item.path).join(',');
      
      // Only process if we haven't seen this exact batch before
      if (processedBatchRef.current !== batchId) {
        // Mark this batch as processed
        processedBatchRef.current = batchId;
        
        // Clear any existing media first to prevent duplicates
        setMedia([]);

        alert('🔵 App.tsx: Received shared media update: ' + (sharedMedia ? sharedMedia.length : 0) + ' items');
        const processSharedMedia = async () => {
        // Process each shared media item
        for (const item of sharedMedia) {
          try {
            // Determine if it's an image or video based on MIME type
            const isVideo = item.mimeType?.startsWith('video/') ||
              item.path?.toLowerCase().endsWith('.mp4') ||
              item.path?.toLowerCase().endsWith('.mov') ||
              false;

            const isImage = item.mimeType?.startsWith('image/') ||
              item.path?.toLowerCase().endsWith('.jpg') ||
              item.path?.toLowerCase().endsWith('.jpeg') ||
              item.path?.toLowerCase().endsWith('.png') ||
              item.path?.toLowerCase().endsWith('.heic') ||
              false;

            if (!isVideo && !isImage) {
              console.warn('Unsupported media type:', item.mimeType, 'for path:', item.path);
              continue;
            }

            // Process the file path and content
            let filePath = item.path;
            let fileContent = item.fileContent;
            let fileBlob: Blob | null = null;

            // For iOS file:// URLs, we need to convert them to a format that can be displayed and uploaded
            if (filePath.startsWith('file://')) {
              console.log('Converting file:// URL to displayable format:', filePath);

              // If we have the file content as base64, use it directly
              if (fileContent) {
                // Create a data URL from the file content
                const mimeType = item.mimeType || (isVideo ? 'video/mp4' : 'image/jpeg');
                filePath = `data:${mimeType};base64,${fileContent as string}`;
                console.log('Using file content as data URL');

                // Convert base64 to Blob for upload
                const byteCharacters = atob(fileContent as string);
                const byteArrays: Uint8Array[] = [];
                for (let i = 0; i < byteCharacters.length; i += 512) {
                  const slice = byteCharacters.slice(i, i + 512);
                  const byteNumbers = new Array(slice.length);
                  for (let j = 0; j < slice.length; j++) {
                    byteNumbers[j] = slice.charCodeAt(j);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  byteArrays.push(byteArray);
                }
                fileBlob = new Blob(byteArrays, { type: mimeType });
              } else {
                // Try to use the Capacitor Filesystem to read the file
                try {
                  const { Filesystem } = await import('@capacitor/filesystem');
                  const fileData = await Filesystem.readFile({
                    path: filePath
                  });

                  if (fileData && fileData.data) {
                    // Create a data URL from the file content
                    const mimeType = item.mimeType || (isVideo ? 'video/mp4' : 'image/jpeg');
                    filePath = `data:${mimeType};base64,${fileData.data as string}`;
                    console.log('Successfully read file and created data URL');

                    // Convert base64 to Blob for upload
                    const byteCharacters = atob(fileData.data as string);
                    const byteArrays: Uint8Array[] = [];
                    for (let i = 0; i < byteCharacters.length; i += 512) {
                      const slice = byteCharacters.slice(i, i + 512);
                      const byteNumbers = new Array(slice.length);
                      for (let j = 0; j < slice.length; j++) {
                        byteNumbers[j] = slice.charCodeAt(j);
                      }
                      const byteArray = new Uint8Array(byteNumbers);
                      byteArrays.push(byteArray);
                    }
                    fileBlob = new Blob(byteArrays, { type: mimeType });
                  }
                } catch (readError) {
                  console.error('Error reading file:', readError);
                  // If we can't read the file, we'll try to use the path directly
                  // but it may not work in the browser due to security restrictions
                }
              }
            }

            if (isVideo) {
              // For videos, use the existing uppy workflow
              if (fileBlob) {
                console.log('Adding video from shared media to uppy');
                uppy.addFile({
                  source: 'local',
                  name: `${userProfileId || 'shared'}_${Date.now()}`,
                  type: item.mimeType || 'video/mp4',
                  data: fileBlob
                });
              } else {
                console.warn('Could not create blob for video upload');
                // Add a placeholder for the video
                const uniqueId = `shared-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                setMedia(prev => [
                  ...prev,
                  {
                    id: uniqueId,
                    type: 'video',
                    url: filePath,
                    uploading: false,
                    fileContent: fileContent
                  }
                ]);
              }
            } else if (isImage && fileBlob) {
              // For images, follow the normal image upload workflow
              try {
                // Create a File object from the Blob
                const fileName = filePath.split('/').pop() || `shared-image-${Date.now()}.jpg`;
                const file = new File([fileBlob], fileName, { type: item.mimeType || 'image/jpeg' });

                // Fetch presigned URL
                const { data: presignedUrls } = await api.get('/posts/presigned-url', {
                  headers: {
                    'url-count': 1,
                  },
                });

                // Create new media item
                const newMedia = {
                  id: presignedUrls[0].id,
                  type: 'image' as const,
                  url: URL.createObjectURL(fileBlob),
                  uploading: true,
                  file: file
                };

                setMedia(prev => [...prev, newMedia]);

                // Upload file
                await uploadToR2(presignedUrls[0].url, file);
                setMedia(prev =>
                  prev.map(mediaItem =>
                    mediaItem.id === newMedia.id
                      ? { ...mediaItem, uploading: false }
                      : mediaItem,
                  ),
                );

                console.log(`Added image from shared media and uploaded to R2: ${presignedUrls[0].id}`);
              } catch (uploadError) {
                console.error('Error uploading shared image:', uploadError);
                // Add a placeholder for the image if upload fails
                const uniqueId = `shared-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                setMedia(prev => [
                  ...prev,
                  {
                    id: uniqueId,
                    type: 'image',
                    url: filePath,
                    uploading: false,
                    fileContent: fileContent,
                    error: true
                  }
                ]);
              }
            } else {
              // Fallback for cases where we couldn't create a blob
              console.warn('Could not create blob for media upload, using placeholder');
              const uniqueId = `shared-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              setMedia(prev => [
                ...prev,
                {
                  id: uniqueId,
                  type: isVideo ? 'video' : 'image',
                  url: filePath,
                  uploading: false,
                  fileContent: fileContent
                }
              ]);
            }
          } catch (error) {
            console.error('Error processing shared media:', error);
          }
        }
      };

      processSharedMedia();
      }
    }
  }, [isOpen, sharedMedia, uppy, userProfileId, setMedia]);
}
